/**
 * Analytics API Service
 * Centralized service for all analytics API calls
 * Supports both Titan backend and external API sources
 */

import { API_BASE_URL } from '../config/api.config';

import * as externalApi from './externalApi';
import * as rfxcelApi from './rfxcelAnalyticsApi';

/**
 * Generic fetch wrapper with error handling
 */
const fetchData = async (endpoint, options = {}) => {
  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || 'API request failed');
    }

    return data.data;
  } catch (error) {
    console.error(`API Error (${endpoint}):`, error);
    throw error;
  }
};

/**
 * Get dashboard statistics
 * @param {string} timeRange - '1h' | '24h' | 'week' | 'month'
 */
export const getDashboardStats = async (timeRange = '24h') => {
  return fetchData(`/analytics/stats?timeRange=${timeRange}`);
};

/**
 * Get shipment frequency data
 * @param {number} days - Number of days to look back
 * @param {string} granularity - 'hourly' | 'daily' | 'weekly' | 'monthly'
 */
export const getShipmentFrequency = async (days = 30, granularity = 'daily') => {
  return fetchData(`/analytics/shipment-frequency?days=${days}&granularity=${granularity}`);
};

/**
 * Get quantity distribution
 */
export const getQuantityDistribution = async () => {
  return fetchData('/analytics/quantity-distribution');
};

/**
 * Get product mix (top categories)
 */
export const getProductMix = async () => {
  return fetchData('/analytics/product-mix');
};

/**
 * Get shipments by location
 * @param {number} limit - Number of top locations to return
 */
export const getShipmentsByLocation = async (limit = 10) => {
  return fetchData(`/analytics/shipments-by-location?limit=${limit}`);
};

/**
 * Get top partners
 * @param {number} limit - Number of top partners to return
 */
export const getTopPartners = async (limit = 5) => {
  return fetchData(`/analytics/top-partners?limit=${limit}`);
};

/**
 * Get exception analysis
 * @param {number} days - Number of days to look back
 */
export const getExceptions = async (days = 7) => {
  return fetchData(`/analytics/exceptions?days=${days}`);
};

/**
 * Get processing time distribution
 */
export const getProcessingTime = async () => {
  return fetchData('/analytics/processing-time');
};

/**
 * Get product trends
 * @param {number} days - Number of days to look back
 */
export const getProductTrends = async (days = 7) => {
  return fetchData(`/analytics/product-trends?days=${days}`);
};

/**
 * Get route performance metrics
 * @param {number} limit - Number of top routes to return
 */
export const getRoutePerformance = async (limit = 2) => {
  return fetchData(`/analytics/route-performance?limit=${limit}`);
};

/**
 * Get system performance metrics
 * @param {number} hours - Number of hours to look back
 */
export const getSystemPerformance = async (hours = 24) => {
  return fetchData(`/analytics/system-performance?hours=${hours}`);
};

/**
 * Get recent shipments
 * @param {number} limit - Number of records per page
 * @param {number} offset - Number of records to skip
 */
export const getRecentShipments = async (limit = 10, offset = 0) => {
  return fetchData(`/analytics/recent-shipments?limit=${limit}&offset=${offset}`);
};

/**
 * Fetch all dashboard data at once
 * Returns an object with all the data needed for the dashboard
 * @param {string} source - 'titan' or 'external' - determines which API to use
 */
export const fetchAllDashboardData = async (source = 'titan') => {
  try {
    // If RFxcel source is selected, use RFxcel API
    if (source === 'rfxcel') {
      console.log('Fetching dashboard data from RFxcel...');
      return await rfxcelApi.fetchAllRfxcelDashboardData();
    }

    // If external source is selected, use external API
    if (source === 'external') {
      console.log('Fetching dashboard data from external API...');
      return await externalApi.fetchAllExternalDashboardData();
    }

    // Default: Use Titan backend
    console.log('Fetching dashboard data from Titan backend...');
    const [
      stats,
      shipmentFrequency,
      quantityDistribution,
      productMix,
      shipmentsByLocation,
      topPartners,
      exceptions,
      processingTime,
      productTrends,
      routePerformance,
      systemPerformance,
      recentShipments,
    ] = await Promise.all([
      getDashboardStats('24h').catch(() => null),
      getShipmentFrequency(30, 'daily').catch(() => null),
      getQuantityDistribution().catch(() => null),
      getProductMix().catch(() => null),
      getShipmentsByLocation(8).catch(() => null),
      getTopPartners(5).catch(() => null),
      getExceptions(7).catch(() => null),
      getProcessingTime().catch(() => null),
      getProductTrends(7).catch(() => null),
      getRoutePerformance(2).catch(() => null),
      getSystemPerformance(24).catch(() => null),
      getRecentShipments(5, 0).catch(() => null),
    ]);

    return {
      stats,
      shipmentFrequency,
      quantityDistribution,
      productMix,
      shipmentsByLocation,
      topPartners,
      exceptions,
      processingTime,
      productTrends,
      routePerformance,
      systemPerformance,
      recentShipments,
      source: 'titan'
    };
  } catch (error) {
    console.error('Error fetching dashboard data:', error);
    throw error;
  }
};

export default {
  getDashboardStats,
  getShipmentFrequency,
  getQuantityDistribution,
  getProductMix,
  getShipmentsByLocation,
  getTopPartners,
  getExceptions,
  getProcessingTime,
  getProductTrends,
  getRoutePerformance,
  getSystemPerformance,
  getRecentShipments,
  fetchAllDashboardData,
};
